import { Component, HostListener, OnInit, ViewChild } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { BehaviorSubject, filter, firstValueFrom, Subscription } from 'rxjs';
import { CustomModalComponent } from 'src/app/components/custom-modal/custom-modal.component';
import { AuthenticationContants } from 'src/app/constants/authentication-constants';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { ConceptInvoicingModel } from 'src/app/models/invoicing/concept-invoicing.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { ConceptInvoicingTableModel } from 'src/app/models/tables/invoicing/concept-invoicing-table.model';
import { WindowService } from 'src/app/services/authentication/window.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { ConceptInvoicingService } from 'src/app/services/invoicing/concept-invoicing.service';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import { ImportConceptInvoicingComponent } from '../import-concept-invoicing/import-concept-invoicing.component';
import { RegisterConceptInvoicingComponent } from '../register-concept-invoicing/register-concept-invoicing.component';
import { UserModel } from 'src/app/models/authentication/user.model';
import { ProfileUserEnums } from 'src/app/enum/profile-user-enums';
import { OptionWindowModel } from 'src/app/models/authentication/option-window.model';
import { OptionWindow } from 'src/app/enum/option-window-enums';
import { UserAuthenticatedService } from 'src/app/services/global-services/user-authenticated.service';
import { WindowEnums } from 'src/app/enum/window-enums';
import { OptionWindowService } from 'src/app/services/authentication/option-window.service';
import { Router } from '@angular/router';
import { ConceptInvoicingTypeEnums } from 'src/app/enum/concept-invoicing-enums';

@Component({
  selector: 'app-concept-invoicing',
  templateUrl: './concept-invoicing.component.html',
  styleUrls: ['./concept-invoicing.component.scss'],
  standalone: false
})
export class ConceptInvoicingComponent implements OnInit {

  @ViewChild('conceptInvoicingModal') conceptInvoicingModalComponent?: CustomModalComponent | null = null;
  @ViewChild('conceptInvoicingImportModal') conceptInvoicingImportModalComponent?: CustomModalComponent | null = null;

  ascendant: boolean = true;
  columnSort: string | null = null;
  data: PagedResponseModel<ConceptInvoicingTableModel> = new PagedResponseModel<ConceptInvoicingTableModel>();
  filters: ColumnResponseModel[] = [];
  header: ColumnResponseModel[] = [];
  conceptInvoicingType: number = 1;
  inputsForConceptInvoicing$ = new BehaviorSubject<any>({
    conceptInvoicingModel: new ConceptInvoicingModel,
  });
  inputsForImportConceptInvoicing$ = new BehaviorSubject<any>({
    conceptInvoicingType: 1,
  });
  isOpened: string | undefined = undefined;
  limit: number = 10;
  nameWindow: string = 'Conceptos de Facturación';
  page: number = 1;
  permissionUpdatable: boolean = false;
  permissionsUser: Array<OptionWindowModel> = new Array<OptionWindowModel>();
  projectName = ProjectName.projectName;
  subscription: Subscription;
  tableActions: TableAction[] = [
    {
      label: 'Editar',
      icon: 'pi pi-pencil',
      action: 'edit'
    }, {
      label: 'Eliminar',
      icon: 'pi pi-trash',
      action: 'delete'
    },
  ];
  url: string = 'concept-invoicing';
  userAuthenticated: UserModel = new UserModel();
  componentRegisterConceptInvoicing = RegisterConceptInvoicingComponent;
  componentConceptInvoicingImportModal = ImportConceptInvoicingComponent;

  constructor(private confirmDialogService: ConfirmDialogService,
    private conceptInvoicingService: ConceptInvoicingService,
    private headerService: HeaderService,
    private title: Title,
    private optionWindowService: OptionWindowService,
    private router: Router,
    private toastHandlerService: ToastHandlerService,
    private userAuthenticatedService: UserAuthenticatedService,
    private windowService: WindowService) { }

  async ngOnInit() {
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    this.headerService.setTitle(this.nameWindow);
    const filters = localStorage.getItem(`${this.url}`);
    if (filters) this.filters = JSON.parse(filters);
    this.getPermission();
    await Promise.all([
      this.getColumnsForGeneralConsultation(),
      this.getAllConceptInvoicingsWithPagination(),
    ]);
    this.subscription = this.inputsForConceptInvoicing$.subscribe(async ({ type }) => {
      if (!type) return;
      switch (type) {
        case 'reload':
          await this.getAllConceptInvoicingsWithPagination();
          break;
        default:
          console.warn('Tipo de evento desconocido:', type);
      }
    });
  }

  @HostListener('window:beforeunload')
  beforeUnloadHandler() {
    this.ngOnDestroy();
  }

  ngOnDestroy() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      const window = windowsOpened.findIndex(x => x === this.url);
      if (window != -1) {
        windowsOpened.splice(window, 1);
        localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      }
    }
  }

  async delete(conceptInvoicing: ConceptInvoicingTableModel) {
    const textName = this.conceptInvoicingType === ConceptInvoicingTypeEnums.compra ? 'Compra' : 'Facturación';
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast('warning', `El usuario no tiene permisos para eliminar conceptos de ${textName}.
        Comuníquese con el administrador del sistema.`);
      return;
    }
    const accepted = await this.confirmDialogService.confirm(`¿Realmente desea eliminar el concepto de ${textName}?`);
    if (!accepted) { return; }
    try {
      if (this.conceptInvoicingType === ConceptInvoicingTypeEnums.compra) {
        await firstValueFrom(this.conceptInvoicingService.deletePurchaseConcept(conceptInvoicing.conceptInvoicingId));
      } else {
        await firstValueFrom(this.conceptInvoicingService.deleteConceptInvoicing(conceptInvoicing.conceptInvoicingId));
      }
      await this.getAllConceptInvoicingsWithPagination();
      this.toastHandlerService.showToast('success', `El concepto de ${textName} fue eliminado exitosamente.`);
    } catch (error) {
      console.error(`Error al eliminar el concepto de ${textName}.`, error);
    }
  }

  async exportGeneralConsultation() {
    const message = this.conceptInvoicingType === ConceptInvoicingTypeEnums.venta ? 'Facturación' : 'Compra';
    try {
      let response;
      if (this.conceptInvoicingType === ConceptInvoicingTypeEnums.compra) {
        response = await firstValueFrom(this.conceptInvoicingService.exportPurchaseGeneralConsultation(this.filters, this.columnSort, this.ascendant));
      } else {
        response = await firstValueFrom(this.conceptInvoicingService.exportGeneralConsultation(this.filters, this.columnSort, this.ascendant));
      }
      const blob = response;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Conceptos de ${message}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(`Error al exportar los conceptos de ${message}.`, error);
    }
  }

  async getAllConceptInvoicingsWithPagination() {
    const message = this.conceptInvoicingType === ConceptInvoicingTypeEnums.venta ? 'Facturación' : 'Compra';
    try {
      if (this.conceptInvoicingType === ConceptInvoicingTypeEnums.compra) {
        this.data = await firstValueFrom(this.conceptInvoicingService.getAllPurchaseConceptInvoicingsWithPagination(this.page, this.limit,
          this.columnSort, this.ascendant, this.filters));
      } else {
        this.data = await firstValueFrom(this.conceptInvoicingService.getAllConceptInvoicingsWithPagination(this.page, this.limit,
          this.columnSort, this.ascendant, this.filters));
      }
    } catch (error) {
      console.error(`Error al buscar los conceptos de ${message}.`, error);
    }
  }

  async getColumnsForGeneralConsultation() {
    const message = this.conceptInvoicingType === ConceptInvoicingTypeEnums.venta ? 'Facturación' : 'Compra';
    try {
      if (this.header.length === 0) {
        if (this.conceptInvoicingType === ConceptInvoicingTypeEnums.compra) {
          this.header = await firstValueFrom(this.conceptInvoicingService.getColumnsForPurchaseConceptGeneralConsultation());
        } else {
          this.header = await firstValueFrom(this.conceptInvoicingService.getColumnsForGeneralConsultation());
        }
      }
    } catch (error) {
      console.error(`Error al buscar las columnas para la consulta general de los conceptos de ${message}.`, error);
    }
  }

  async getPermission() {
    const url = this.router.url as string;
    if (url && url !== '/') {
      this.url = url.startsWith('/') ? url.substring(1) : url;
    }

    this.conceptInvoicingType = this.url === 'purchase-concept' ? ConceptInvoicingTypeEnums.compra : ConceptInvoicingTypeEnums.venta;
    this.nameWindow = this.conceptInvoicingType === ConceptInvoicingTypeEnums.compra ? 'Conceptos de Compra' : 'Conceptos de Facturación';
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    this.headerService.setTitle(this.nameWindow);

    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      this.isOpened = windowsOpened.find(x => x === this.url);
      windowsOpened.push(this.url);
      localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      if (this.isOpened) {
        this.toastHandlerService.showToast('error', `No se permite tener dos ventanas/pestañas de ${this.nameWindow} abiertas.`);
      }
    } else {
      localStorage.clear();
    }
    await Promise.all([
      this.getPermissionsForUserByWindow(),
      this.getPermissionProfileByUrl(),
      this.getUserAuthenticated()
    ]);
  }

  async getPermissionsForUserByWindow() {
    this.userAuthenticated = await firstValueFrom(
      this.userAuthenticatedService.userObservable.pipe(
        filter(user => user && user.userId !== 0)
      )
    );
    this.permissionsUser = await firstValueFrom(this.optionWindowService.getPermissionsForUserByWindow(WindowEnums.ConceptInvoicing));
  }

  async getPermissionProfileByUrl() {
    try {
      this.permissionUpdatable = await firstValueFrom(this.windowService.getPermissionProfileByUrl(this.url));
    } catch (error) {
      console.error('Error al buscar los permisos del perfil.', error);
    }
  }

  async getUserAuthenticated() {
    try {
      this.userAuthenticated = await firstValueFrom(
        this.userAuthenticatedService.userObservable.pipe(
          filter(user => user && user.userId !== 0)
        )
      );
    } catch (error) {
      console.error('Error al obtener el usuario autenticado.', error);
    }
  }

  onAction(event: { action: string, item?: ConceptInvoicingTableModel, selectedItems?: number[] }) {
    if (event.action === 'edit' && event.item) {
      const conceptInvoicingModel = new ConceptInvoicingModel;
      conceptInvoicingModel.conceptInvoicingId = event.item.id;
      conceptInvoicingModel.code = event.item.code;
      conceptInvoicingModel.name = event.item.name;
      this.updateInput(conceptInvoicingModel)
    }
    if (event.action === 'delete' && event.item) {
      this.delete(event.item);
    }
  }

  async onExport() {
    await this.exportGeneralConsultation();
  }

  async onImport() {
    const message = this.conceptInvoicingType === ConceptInvoicingTypeEnums.venta ? 'Facturación' : 'Compra';
    if (this.userAuthenticated.profileUser.code == ProfileUserEnums.administrador || this.permissionsUser.find(x => x.code == OptionWindow.Importar)) {
      if (this.permissionUpdatable) {
        this.inputsForImportConceptInvoicing$.next({
          conceptInvoicingType: this.conceptInvoicingType
        });
        this.conceptInvoicingImportModalComponent && this.conceptInvoicingImportModalComponent.show();
      } else {
        this.toastHandlerService.showToast('warning', `El usuario no tiene permisos para la edición de conceptos de ${message.toLowerCase()}.
          Comuníquese con el administrador del sistema.`);
      }
    } else {
      this.toastHandlerService.showToast('warning', `El usuario no tiene permisos para importar conceptos de ${message.toLowerCase()},
        comuniquese con el administrador del sistema.`);
    }
  }

  async onFilterChange(event: { filters: any }) {
    const filters = event.filters.map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index).map((item: any) => item.value);
    this.filters = this.removeDuplicates(filters);
    this.filters.forEach((filter: any) => this.processFilter(filter));
  }

  async onPageChange(event: { page: number; pageSize: number; sortField?: string; sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField ?? null;
    this.ascendant = event.sortOrder === 1;
    await this.getAllConceptInvoicingsWithPagination();
  }

  openModalConceptInvoicing() {
    this.updateInput(new ConceptInvoicingModel());
  }

  processFilter(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0) {
      this.filters = this.filters.filter(f => f.propertyName !== propertyName);
    } else {
      const existingFilter = this.filters.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }

  updateInput(conceptInvoicingContract: ConceptInvoicingModel) {
    conceptInvoicingContract.conceptInvoicingType = this.conceptInvoicingType;
    this.inputsForConceptInvoicing$.next({
      conceptInvoicingModel: conceptInvoicingContract,
      conceptInvoicingType: this.conceptInvoicingType
    });
    this.conceptInvoicingModalComponent && this.conceptInvoicingModalComponent.show();
  }
}
